import SceneObject from "../../../lib/services/scene/sceneobject";
import Puppy from "./puppy";
import Frisbee from "../frisbee/frisbee";
import JumpController from "./jump_controller";
import JumpGraphService from "../../../lib/services/collision/jump_graph_service";
import { PuppyClips } from "./puppyclips";
import {FetchPlan} from "./fetch_types"

const EAT_SPOT = new Float32Array([
  0.5132098197937012,
  0,
  6.010136127471924,
]);

// --- Tunables ---------------------------------------------------------------

const CATCH_RADIUS_XZ = 0.55;       // XZ distance for a mid-air catch
const CATCH_RADIUS_Y = 0.45;        // |dy| between puppy head and disc

const PUPPY_RUN_SPEED = 2.6;        // units/sec, approx Run_F_RM speed

const PLAN_DELAY_DEFAULT = 0.15;    // small pause before throw fires
const PLAN_DELAY_FROM_REST = 0.45;  // longer if puppy was sitting/lying

const INTERCEPT_PREFER_LOW_Y = 0.4; // we prefer points where disc is near this Y
const INTERCEPT_SAMPLES = 30;

const ARRIVE_RADIUS = 0.18;
const PICKUP_HOLD_SECONDS = 0.35;
const DROP_HOLD_SECONDS = 0.5;

const COST_PER_JUMP_EDGE = 0.9;     // rough seconds-per-hop estimate

const FRISBEE_LAND_Y = 0.02;
const DROP_FORWARD = 0.35;

const PICKUP_RADIUS_XZ = 0.5;

// --- Types ------------------------------------------------------------------



type State =
  | "idle"
  | "planning"          // brief delay between command and actual throw fire
  | "stay_in_place"     // puppy is on the target cap, waiting for the pass
  | "chase_object"      // climbing toward target cap to catch
  | "chase_disc"        // running on ground toward intercept or landing XZ
  | "picking_up"        // attaching disc, brief animation
  | "returning"         // carrying disc to EAT_SPOT
  | "dropping";         // placing disc, brief settle

export default class FrisbeeFetchController extends SceneObject {
  private puppy: Puppy;
  private frisbee: Frisbee;
  private jump_controller: JumpController;
  private graph: JumpGraphService;

  private state: State = "idle";
  private timer: number = 0;

  private pending_plan: FetchPlan | null = null;
  private current_plan: FetchPlan | null = null;
  private throw_fire_callback: (() => void) | null = null;

  private last_landing_xz: Float32Array | null = null;
  public enabled:boolean = false;
  constructor(
    key: string,
    puppy: Puppy,
    frisbee: Frisbee,
    jump_controller: JumpController,
    graph: JumpGraphService
  ) {
    super(key);
    this.puppy = puppy;
    this.frisbee = frisbee;
    this.jump_controller = jump_controller;
    this.graph = graph;
  }

  // ---- Public API --------------------------------------------------------

  /**
   * FrisbeeController calls this on throw release. Passes the full flight plan
   * and a callback to actually fire the throw on the frisbee. The fetch
   * controller decides how long to wait before firing, and which strategy
   * the puppy will use.
   */
  begin_planning(plan: FetchPlan, throw_fire: () => void): void {
    this.pending_plan = plan;
    this.throw_fire_callback = throw_fire;
    this.timer = 0;
    this.state = "planning";
    this.last_landing_xz = new Float32Array([plan.landing_xz[0], 0, plan.landing_xz[2]]);
  }

  is_player_locked(): boolean {
    return this.state !== "idle";
  }

  get_state(): State {
    return this.state;
  }

  // ---- Tick --------------------------------------------------------------

  tick(delta_time: number): void {
     if(!this.enabled) return;
    switch (this.state) {
      case "idle":
        break;
      case "planning":
        this.tick_planning(delta_time);
        break;
      case "stay_in_place":
        this.tick_stay_in_place();
        break;
      case "chase_object":
        this.tick_chase_object();
        break;
      case "chase_disc":
        this.tick_chase_disc();
        break;
      case "picking_up":
        this.tick_picking_up(delta_time);
        break;
      case "returning":
        this.tick_returning();
        break;
      case "dropping":
        this.tick_dropping(delta_time);
        break;
    }

    super.tick(delta_time);
  }

  // ---- planning ----------------------------------------------------------

  private tick_planning(delta_time: number): void {
    this.timer += delta_time;

    const delay = this.compute_plan_delay();

    if (this.timer < delay) return;

    // Fire the throw now and switch to the chosen strategy.
    const plan = this.pending_plan!;
    this.current_plan = plan;
    this.pending_plan = null;

    const fire = this.throw_fire_callback!;
    this.throw_fire_callback = null;
    fire();

    this.enter_strategy_for(plan);
  }

  private compute_plan_delay(): number {
    // Quick heuristic: if the puppy's nav posture is rest-like, give him
    // longer to get up before launching.
    const posture = this.puppy.navigator.get_posture
      ? this.puppy.navigator.get_posture()
      : "idle";

    if (posture === "sitting" || posture === "lying" || posture === "sleeping") {
      return PLAN_DELAY_FROM_REST;
    }
    return PLAN_DELAY_DEFAULT;
  }

  private enter_strategy_for(plan: FetchPlan): void {
    const loc = this.jump_controller.get_location();

    // Case 1: Puppy is on the target cap → stay in place.
    if (plan.target_kind === "object_pass" &&
        loc.kind === "object" &&
        loc.node_id === plan.target_node_id) {
      this.enter_stay_in_place();
      return;
    }

    // Case 2: Object throw, puppy elsewhere → try to climb to target.
    if (plan.target_kind === "object_pass") {
      this.enter_chase_object(plan.target_node_id!);
      return;
    }

    // Case 3 & 4: Ground throw — single chase state, branches on location.
    this.enter_chase_disc(plan);
  }

  // ---- stay_in_place -----------------------------------------------------

  private enter_stay_in_place(): void {
    // Optional: play a "watch" anim. For now keep current loop.
    this.state = "stay_in_place";
  }

  private tick_stay_in_place(): void {
    if (this.try_catch()) return;

    if (!this.frisbee.is_flying()) {
      // Disc passed without catch; it'll have landed past the cap on ground.
      this.fallback_to_chase_disc_after_miss();
    }
  }

  // ---- chase_object ------------------------------------------------------

  private enter_chase_object(target_node_id: string): void {
    this.jump_controller.set_active_target(target_node_id);
    this.state = "chase_object";
  }

  private tick_chase_object(): void {
    if (this.try_catch()) return;

    const plan = this.current_plan!;
    if (plan.target_kind !== "object_pass" || plan.pass_t === null) {
      this.fallback_to_chase_disc_after_miss();
      return;
    }

    // Have we landed on the target cap?
    const loc = this.jump_controller.get_location();
    if (loc.kind === "object" && loc.node_id === plan.target_node_id) {
      this.enter_stay_in_place();
      return;
    }

    // Feasibility check: can we still make it before the pass moment?
    const time_remaining =
      plan.pass_t * plan.flight_duration - this.frisbee.get_flight_elapsed_seconds();

    const remaining_edges = this.jump_controller.get_remaining_edge_count()
      ? this.jump_controller.get_remaining_edge_count()
      : 99;

    if (remaining_edges * COST_PER_JUMP_EDGE > time_remaining) {
      // Give up the climb. Head to the eventual landing spot on the ground.
      this.jump_controller.set_active_target(null);
      this.fallback_to_chase_disc_after_miss();
      return;
    }

    // Otherwise keep climbing; JumpController is handling it.
  }

  // ---- chase_disc (ground intercept / descend then ground) --------------

  private enter_chase_disc(plan: FetchPlan): void {
    const loc = this.jump_controller.get_location();
    const landing = plan.landing_xz;

    if (loc.kind === "object") {
      // Need to descend first; JumpController will descend and then
      // auto-walk to the supplied ground target.
      this.jump_controller.set_ground_intent(landing);
      this.state = "chase_disc";
      return;
    }

    // Already on ground — pick an intercept point on the curve if the disc
    // is still flying; otherwise run straight to the landing XZ.
    const target = this.frisbee.is_flying()
      ? (this.find_intercept_point(plan) ?? landing)
      : landing;

    this.puppy.navigator.move_to_point(
      new Float32Array([target[0], 0, target[2]]),
      "aggressive",
      ARRIVE_RADIUS
    );
    this.state = "chase_disc";
  }

  private tick_chase_disc(): void {
    if (this.try_catch()) return;

    const loc = this.jump_controller.get_location();

    // Still climbing/descending — let JumpController finish.
    if (this.jump_controller.is_busy()) return;
    if (loc.kind === "object") {
      // JumpController settled on an object but we want ground.
      // Push the ground intent again to bring him down.
      this.jump_controller.set_ground_intent(this.current_plan!.landing_xz);
      return;
    }

    const flying = this.frisbee.is_flying();
  const has_dest = this.puppy.navigator.has_destination();
  const fp = this.frisbee.get_world_position();
  const p = this.puppy.transform.position;
  const xz = Math.hypot(fp[0] - p[0], fp[2] - p[2]);

  /*console.log(
    `[fetch] chase_disc flying=${flying} has_dest=${has_dest} xz=${xz.toFixed(2)}`
  );*/


    // On ground. If disc has now landed and the navigator is idle, we may
    // need to walk to the actual landing point (intercept missed).
    if (!this.frisbee.is_flying() && !this.puppy.navigator.has_destination()) {
      const fp = this.frisbee.get_world_position();
      const p = this.puppy.transform.position;
      const xz = Math.hypot(fp[0] - p[0], fp[2] - p[2]);
      if (xz <= PICKUP_RADIUS_XZ ) {
        this.enter_picking_up();
        return;
      }
      // Send him to the disc's resting position.
      this.puppy.navigator.move_to_point(
        new Float32Array([fp[0], 0, fp[2]]),
        "aggressive",
        ARRIVE_RADIUS
      );
    }
  }

    // Used when a strategy gives up and we want to fall back to "go pick up
  // wherever it lands."
  private fallback_to_chase_disc_after_miss(): void {
    if (!this.current_plan) {
      this.state = "idle";
      return;
    }
    this.enter_chase_disc(this.current_plan);
  }

  // ---- picking_up --------------------------------------------------------

  private enter_picking_up(): void {
    this.face_toward(this.frisbee.get_world_position());

    console.log("[fetch] enter_picking_up");
    // Reparent the frisbee to the mouth.
    this.frisbee.stow();
    this.puppy.attach_child(this.frisbee, "frisbee_in_mouth");

    const c = this.puppy.animation_controller_gpu;
    if (c) {
      c.playSequence([
        { clipIndex: PuppyClips.Attack_Bite, blendInSeconds: 0.12, loop: false },
      ]);
    }

    this.timer = 0;
    this.state = "picking_up";
  }

  private tick_picking_up(delta_time: number): void {
    this.timer += delta_time;
    if (this.timer >= PICKUP_HOLD_SECONDS) {
      this.enter_returning();
    }
  }

  // ---- returning ---------------------------------------------------------

  private enter_returning(): void 
  {
        const loc = this.jump_controller.get_location();

        if (loc.kind === "object")
        {
            // Descend the staircase, then walk to EAT_SPOT.
            this.jump_controller.set_ground_intent(EAT_SPOT);
        } 
        else
        {
            this.puppy.navigator.move_to_point(EAT_SPOT, "aggressive", 0.2);
        }

        this.state = "returning";
  }

    private tick_returning(): void 
    {
        // Let the jump controller finish any descent.
        if (this.jump_controller.is_busy()) return;

        const loc = this.jump_controller.get_location();
        const has_dest = this.puppy.navigator.has_destination();
        console.log(`[fetch] returning  loc=${loc.kind} has_dest=${has_dest}`);

        if (loc.kind === "object")
        {
            // We were waiting for the descent; controller settled on a cap. Re-push.
            this.jump_controller.set_ground_intent(EAT_SPOT);
            return;
        }
        // On ground. Done when navigator arrives.
        if (this.puppy.navigator.has_destination()) return;

        this.enter_dropping();
  }


  // ---- dropping ----------------------------------------------------------

  private enter_dropping(): void {
    const p = this.puppy.transform.position;
    const yaw = this.puppy.transform.rotation[1];
    const fwd_x = -Math.sin(yaw);
    const fwd_z =  Math.cos(yaw);

    this.puppy.detach_child(this.frisbee);
    this.frisbee.place_on_ground(new Float32Array([
      p[0] + fwd_x * DROP_FORWARD,
      FRISBEE_LAND_Y,
      p[2] + fwd_z * DROP_FORWARD,
    ]));

    const c = this.puppy.animation_controller_gpu;
    if (c) {
      c.playSequence([
        { clipIndex: PuppyClips.Sitting_start, blendInSeconds: 0.2, loop: false },
        { clipIndex: PuppyClips.Sitting_loop_1, blendInSeconds: 0.2, loop: true },
      ]);
    }

    this.timer = 0;
    this.state = "dropping";
  }

  private tick_dropping(delta_time: number): void {
    this.timer += delta_time;
    if (this.timer >= DROP_HOLD_SECONDS) {
      this.current_plan = null;
      this.last_landing_xz = null;
      this.state = "idle";
    }
  }

  // ---- Helpers -----------------------------------------------------------

  private try_catch(): boolean {
    if (!this.frisbee.is_flying()) return false;

    const disc = this.frisbee.get_world_position();
    const p = this.puppy.transform.position;

    // Approximate head position: a bit above the transform origin.
    const head_y = p[1] + 0.45;
    const dx = disc[0] - p[0];
    const dz = disc[2] - p[2];
    const dy = disc[1] - head_y;

    const xz = Math.hypot(dx, dz);
    if (xz > CATCH_RADIUS_XZ) return false;
    if (Math.abs(dy) > CATCH_RADIUS_Y) return false;

    // Hit — claim the disc.
    this.enter_picking_up();
    return true;
  }

  private find_intercept_point(plan: FetchPlan): Float32Array | null {
    const puppy_pos = this.puppy.transform.position;

    let best: Float32Array | null = null;
    let best_score = -Infinity;

    for (let i = 1; i <= INTERCEPT_SAMPLES; i++) {
      const t = i / INTERCEPT_SAMPLES;
      const p = this.frisbee.sample_curve(t);
      const t_disc = t * plan.flight_duration;

      const dx = p[0] - puppy_pos[0];
      const dz = p[2] - puppy_pos[2];
      const dist = Math.hypot(dx, dz);
      const t_puppy = dist / PUPPY_RUN_SPEED;

      // Must arrive at or before the disc.
      if (t_puppy > t_disc) continue;

      // Score: prefer late-flight + low-Y + small slack.
      const lateness = t;
      const lowness = Math.max(0, 1 - Math.abs(p[1] - INTERCEPT_PREFER_LOW_Y));
      const slack = (t_disc - t_puppy);
      const score = lateness * 0.4 + lowness * 0.4 + slack * 0.2;

      if (score > best_score) {
        best_score = score;
        best = new Float32Array([p[0], p[1], p[2]]);
      }
    }
    return best;
  }

  private face_toward(target: Float32Array): void {
    const p = this.puppy.transform.position;
    const dx = target[0] - p[0];
    const dz = target[2] - p[2];
    if (Math.abs(dx) < 1e-4 && Math.abs(dz) < 1e-4) return;

    const yaw = Math.atan2(-dx, dz);
    this.puppy.transform.rotation[1] = yaw;
    this.puppy.transform.update_matrix();
  }
// Add to FrisbeeFetchController
    update_pass_t_after_fire(pass_t: number): void {
    if (this.current_plan) this.current_plan.pass_t = pass_t;
    }

}